import { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function DocumentTypeManager({ onClose }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoad] = useState(false);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleting, setDel] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoad(true);
    try {
      const { data } = await api.get('/meta/document-types');
      setTypes(data);
    } catch {
      toast.error('Failed to load document types');
    } finally {
      setLoad(false);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) {
      toast.error('Document type name is required');
      return;
    }
    setAdding(true);
    try {
      const { data } = await api.post('/meta/document-types', { name: newName });
      setTypes(p => [...p, data]);
      setNewName('');
      toast.success('Document type added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add');
    } finally {
      setAdding(false);
    }
  };

  const handleEdit = async (id) => {
    if (!editName.trim()) {
      toast.error('Document type name is required');
      return;
    }
    try {
      const { data } = await api.put(`/meta/document-types/${id}`, { name: editName });
      setTypes(p => p.map(t => t.id === id ? data : t));
      setEditing(null);
      setEditName('');
      toast.success('Updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document type?')) return;
    setDel(id);
    try {
      await api.delete(`/meta/document-types/${id}`);
      setTypes(p => p.filter(t => t.id !== id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDel(null);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>Manage Document Types</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {/* Add new type */}
          <div style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--text2)', textTransform: 'uppercase' }}>
              Add New Type
            </h4>
            <div className="grid-2" style={{ alignItems: 'flex-end' }}>
              <div className="form-group">
                <label className="form-label">Document Type Name</label>
                <input
                  className="form-input"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="e.g., Passport, License"
                  disabled={adding}
                />
              </div>
              <button className="btn btn-primary" onClick={handleAdd} disabled={adding} style={{ width: '100%' }}>
                {adding ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>

          {/* List existing types */}
          <div>
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--text2)', textTransform: 'uppercase' }}>
              Existing Types ({types.length})
            </h4>
            {loading ? (
              <p style={{ color: 'var(--text3)', textAlign: 'center' }}>Loading…</p>
            ) : types.length === 0 ? (
              <p style={{ color: 'var(--text3)', textAlign: 'center' }}>No document types yet</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {types.map(type => (
                  <div
                    key={type.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      background: 'var(--surface2)',
                      borderRadius: 'var(--radius)',
                      border: editing === type.id ? '1px solid var(--primary)' : '1px solid var(--border)',
                    }}
                  >
                    {editing === type.id ? (
                      <input
                        className="form-input"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        style={{ flex: 1, marginRight: 8 }}
                      />
                    ) : (
                      <span style={{ fontSize: 14, color: 'var(--text)' }}>{type.name}</span>
                    )}
                    <div style={{ display: 'flex', gap: 6 }}>
                      {editing === type.id ? (
                        <>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleEdit(type.id)}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setEditing(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => { setEditing(type.id); setEditName(type.name); }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            disabled={deleting === type.id}
                            onClick={() => handleDelete(type.id)}
                          >
                            {deleting === type.id ? '…' : 'Delete'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
